<template>
    <div class="mb-5 pb-5">
        <div class="mt-5 flex flex-col md:flex-row justify-between items-start">
            <h1 class="text-3xl text-center md:text-left text-theme-base">
                Perfil de
                <strong class="text-theme-primary">tu negocio</strong>
            </h1>
        </div>
        <div
            class="mt-10 flex flex-col items-center gap-8 md:flex-row md:items-start"
        >
            <div
                class="flex flex-col items-center text-center gap-6 md:flex-shrink-0"
            >
                <url-image-manager
                    :current-url="form.logoIconUrl"
                    default-alt="Logo del negocio"
                    :allow-edit-alt="false"
                    default-size="customImage"
                    :preview-width="180"
                    :preview-height="180"
                    @updated="onLogoIconUpdated"
                />
            </div>

            <div class="w-full flex flex-wrap gap-4">
                <custom-input
                    v-model="form.name"
                    label="Nombre del catálogo"
                    placeholder="Nombre de tu catálogo"
                />
                <custom-textarea
                    v-model="form.businessDescription"
                    label="Descripción del negocio"
                    placeholder="Descripción de tu business"
                    :rows="5"
                />
            </div>
        </div>

        <!-- Redes sociales -->
        <div class="mt-6 pt-6 border-t border-theme-border">
            <social-networks-editor v-model="form.socialNetworks" />
        </div>

        <!-- WhatsApp -->
        <div class="mt-6 pt-6 border-t border-theme-border">
            <h2 class="text-xl text-theme-base">
                Número de
                <strong class="text-theme-primary">WhatsApp</strong>
            </h2>
            <div class="flex flex-wrap mt-6 gap-4">
                <div class="w-full md:w-1/2">
                    <label class="text-sm text-theme-muted block mb-1">
                        Número de WhatsApp
                    </label>
                    <whatsapp-number-input
                        v-model="form.whatsappNumber"
                        :country.sync="form.whatsappCountry"
                        :pinned-country="defaultCountry"
                        :invalid="
                            !isWhatsappNumberValid && whatsappNumberTouched
                        "
                        @blur="whatsappNumberTouched = true"
                    />
                    <p
                        v-if="!isWhatsappNumberValid && whatsappNumberTouched"
                        class="mt-1 text-xs text-theme-danger"
                    >
                        Solo dígitos, sin el indicativo (mínimo 7)
                    </p>
                </div>
            </div>
        </div>

        <div class="flex justify-center mt-10">
            <app-button
                :loading="isLoading"
                color="success"
                size="large"
                @click="updateHeaderInfo"
            >
                Actualizar
            </app-button>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import {
    DEFAULT_WHATSAPP_COUNTRY,
    buildWhatsappNumber,
    isValidWhatsappLocalNumber,
    normalizeWhatsappCountryCode,
    splitWhatsappNumber
} from "@/common/lib/whatsapp";

import CustomInput from "@/components/utils/CustomInput.vue";
import CustomTextarea from "@/components/utils/CustomTextarea.vue";
import UrlImageManager from "@/components/utils/UrlImageManager.vue";
import WhatsappNumberInput from "@/components/utils/WhatsappNumberInput.vue";

import SocialNetworksEditor from "./SocialNetworksEditor.vue";

export default {
    name: "CustomizationHeaderPage",
    components: {
        CustomInput,
        CustomTextarea,
        SocialNetworksEditor,
        UrlImageManager,
        WhatsappNumberInput
    },
    data: () => ({
        isLoading: false,
        whatsappNumberTouched: false,
        defaultCountry: DEFAULT_WHATSAPP_COUNTRY,
        form: {
            logoIconUrl: "",
            name: "",
            businessDescription: "",
            socialNetworks: [],
            whatsappNumber: "",
            whatsappCountry: DEFAULT_WHATSAPP_COUNTRY
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isWhatsappNumberValid() {
            return (
                !this.form.whatsappNumber ||
                isValidWhatsappLocalNumber(
                    this.form.whatsappCountry,
                    this.form.whatsappNumber
                )
            );
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("tenant:GetBrandingSettings", "/app")))
            return;
        await this.loadSettings();
    },
    methods: {
        ...mapActions("tenants", [
            "getTenantBrandingSettings",
            "updateTenantBrandingSettings"
        ]),
        async loadSettings() {
            this.$loading.show();
            try {
                const data = await this.getTenantBrandingSettings();
                this.form.logoIconUrl = data.logoIconUrl || "";
                this.form.name = data.name || "";
                this.form.businessDescription = data.businessDescription || "";
                this.form.socialNetworks = (data.socialNetworks || []).map(
                    (entry) => ({ key: entry.network, url: entry.url })
                );
                this.defaultCountry = normalizeWhatsappCountryCode(
                    data.defaultCountry
                );
                const { country, localNumber } = splitWhatsappNumber(
                    data.whatsappNumber,
                    this.defaultCountry
                );
                this.form.whatsappCountry = country;
                this.form.whatsappNumber = localNumber;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async onLogoIconUpdated(url) {
            this.form.logoIconUrl = url;
            try {
                await this.updateTenantBrandingSettings({
                    logoIconUrl: url
                });
                this.$sound.play("success");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Logo actualizado",
                    text: "El logo ha sido actualizado correctamente.",
                    icon: "success"
                });
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateHeaderInfo() {
            this.whatsappNumberTouched = true;
            if (!this.isWhatsappNumberValid) return;

            this.isLoading = true;
            try {
                const whatsappNumber = buildWhatsappNumber(
                    this.form.whatsappCountry,
                    this.form.whatsappNumber
                );
                const defaultCountry = whatsappNumber
                    ? this.form.whatsappCountry
                    : undefined;
                const socialNetworks = this.form.socialNetworks
                    .map((item) => ({
                        network: item.key,
                        url: (item.url || "").trim()
                    }))
                    .filter((item) => item.url);
                await this.updateTenantBrandingSettings({
                    name: this.form.name,
                    businessDescription: this.form.businessDescription,
                    socialNetworks,
                    whatsappNumber,
                    defaultCountry
                });
                if (defaultCountry) this.defaultCountry = defaultCountry;
                this.isLoading = false;
                this.$sound.play("success");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    text: "La información de tu negocio ha sido guardada.",
                    icon: "success"
                });
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
